import { createContext, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { useMatches, useLocation, useNavigationType, type UIMatch } from 'react-router-dom';
import { useGetRouteLabel } from 'utils/hooks/breadcrumbsProvider/useGetRouteLabel';
import { useExtraCrumbs } from 'utils/hooks/breadcrumbsProvider/useExtraCrumbs';

export type CrumbItem = {
  id: string;
  path: string;
  label: React.ReactNode;
};

export type RouteHandle = {
  breadcrumb?: React.ReactNode | ((match: UIMatch) => React.ReactNode);
  hideBreadcrumbs?: boolean;
  excludeFromBreadcrumbs?: boolean;
  systemType?: 'Flow' | 'Helios' | 'Core';
  segmentType?: 'module' | 'childModule' | 'agenda' | 'record';
  actionRequired?: boolean;
};

type BreadcrumbContextType = {
  setLabel: (routeId: string, label: React.ReactNode) => void;
  insert: (crumb: CrumbItem) => void;
  remove: (id: string[]) => void;
  extras: Record<string, CrumbItem>;
  removeAllExtras: () => void;
  clearExtras: () => void;
  restoreLatestExtra: () => void;
  restoreAllRemovedExtras: () => void;
  hide: (routeId?: string) => void;
  show: (routeId?: string) => void;
  items: CrumbItem[];
  lastCrumb: CrumbItem | null;
  baseLength: number;
  isVisible: boolean;
  removedExtras: CrumbItem[];
  pushRemoved: (crumb: CrumbItem) => void;
  pushRemovedByIds: (ids: string[]) => void;
  clickedBreadcrumb: boolean;
  setClickedBreadcrumb: (value: boolean) => void;
  clickedBreadcrumbsAndThenBack: boolean;
  setClickedBreadcrumbsAndThenBack: (value: boolean) => void;
};

const BreadcrumbsContext = createContext<BreadcrumbContextType | null>(null);

export const BreadcrumbsProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const matches = useMatches();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [labels, setLabels] = useState<Record<string, React.ReactNode>>({});
  const [hiddenRoutes, setHiddenRoutes] = useState<Set<string>>(new Set());
  const { getLabel } = useGetRouteLabel();
  const {
    extras,
    insert,
    remove,
    pushRemoved,
    pushRemovedByIds,
    removeAllExtras,
    clearExtras,
    removedCrumbs,
    setRemovedCrumbs,
    clickedBreadcrumb,
    setClickedBreadcrumb,
    restoreAllRemovedExtras,
    restoreLatestExtra,
    clickedBreadcrumbsAndThenBack,
    setClickedBreadcrumbsAndThenBack,
  } = useExtraCrumbs();

  useEffect(() => {
    if ((navigationType === 'PUSH' || navigationType === 'REPLACE') && !clickedBreadcrumb) {
      setRemovedCrumbs([]);
    }
  }, [navigationType, location.pathname, clickedBreadcrumb, setRemovedCrumbs]);

  const setLabel = useCallback((routeId: string, label: React.ReactNode) => {
    setLabels((prev) => {
      if (!label) {
        const { [routeId]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [routeId]: label };
    });
  }, []);

  const hide = useCallback(
    (routeId?: string) => {
      if (routeId) {
        setHiddenRoutes((prev) => new Set(prev).add(routeId));
      } else {
        const currentId = matches[matches.length - 1]?.id;
        if (currentId) {
          setHiddenRoutes((prev) => new Set(prev).add(currentId));
        }
      }
    },
    [matches]
  );

  const show = useCallback(
    (routeId?: string) => {
      if (routeId) {
        setHiddenRoutes((prev) => {
          const next = new Set(prev);
          next.delete(routeId);
          return next;
        });
      } else {
        const currentId = matches[matches.length - 1]?.id;
        if (currentId) {
          setHiddenRoutes((prev) => {
            const next = new Set(prev);
            next.delete(currentId);
            return next;
          });
        }
      }
    },
    [matches]
  );

  const base = useMemo(() => {
    const filteredMatches = matches.filter((m) => {
      if (m.pathname === '/') return false;

      const handle = m.handle as RouteHandle | undefined;
      if (handle?.excludeFromBreadcrumbs) return false;

      return true;
    });

    const items: CrumbItem[] = [];

    for (const m of filteredMatches) {
      const handle = m.handle as RouteHandle | undefined;
      const handleCrumb = handle?.breadcrumb;
      const handleSegmentType = handle?.segmentType;

      let label: React.ReactNode = labels[m.id] ?? (typeof handleCrumb === 'function' ? handleCrumb(m) : handleCrumb);

      if (label == null) {
        label = getLabel(handleSegmentType || 'agenda');
      }

      items.push({ id: m.id, path: m.pathname, label });
    }

    return items;
  }, [matches, labels, getLabel]);

  const isVisible = useMemo(() => {
    const currentMatch = matches[matches.length - 1];
    if (!currentMatch) return true;

    if (hiddenRoutes.has(currentMatch.id)) return false;

    const handle = currentMatch.handle as RouteHandle | undefined;
    if (handle?.hideBreadcrumbs) return false;

    return true;
  }, [matches, hiddenRoutes]);

  const items = useMemo(() => {
    if (!isVisible) return [];

    const composed = [...base];

    const extrasArray = Object.values(extras);
    if (extrasArray.length > 0 && base.length > 0) {
      const insertIndex = composed.length - 1;
      for (let i = 0; i < extrasArray.length; i++) {
        const ex = extrasArray[i];
        composed.splice(insertIndex + i, 0, { id: ex.id, label: ex.label, path: ex.path });
      }
    } else if (extrasArray.length > 0) {
      for (const ex of extrasArray) {
        composed.push({ id: ex.id, label: ex.label, path: ex.path });
      }
    }

    return composed;
  }, [base, extras, isVisible]);

  const lastCrumb = useMemo(() => {
    return items && items.length > 0 ? items[items.length - 1] : null;
  }, [items]);

  const value = useMemo<BreadcrumbContextType>(
    () => ({
      setLabel,
      insert,
      remove,
      extras,
      clearExtras,
      removeAllExtras,
      restoreLatestExtra,
      restoreAllRemovedExtras,
      hide,
      show,
      items,
      lastCrumb,
      baseLength: base.length,
      isVisible,
      removedExtras: removedCrumbs,
      pushRemoved,
      pushRemovedByIds,
      clickedBreadcrumb,
      setClickedBreadcrumb,
      clickedBreadcrumbsAndThenBack,
      setClickedBreadcrumbsAndThenBack,
    }),
    [
      setLabel,
      insert,
      remove,
      extras,
      clearExtras,
      removeAllExtras,
      restoreLatestExtra,
      restoreAllRemovedExtras,
      hide,
      show,
      items,
      lastCrumb,
      base.length,
      isVisible,
      removedCrumbs,
      pushRemoved,
      pushRemovedByIds,
      clickedBreadcrumb,
      setClickedBreadcrumb,
      clickedBreadcrumbsAndThenBack,
      setClickedBreadcrumbsAndThenBack,
    ]
  );

  return <BreadcrumbsContext.Provider value={value}>{children}</BreadcrumbsContext.Provider>;
};

export function useBreadcrumbs() {
  const ctx = useContext(BreadcrumbsContext);
  if (!ctx) throw new Error('useBreadcrumbs must be used within <BreadcrumbsProvider>');
  return ctx;
}
